import { FiCalendar, FiMail, FiActivity, FiBell } from "react-icons/fi";
import { Student } from "../../../types";
import utils from "../../../helpers/utils";

interface StudentCardProps {
  student: Student;
  validityStatus: { color: string; label: string };
  onManageClick: () => void;
  onAvaliationClick: () => void;
  onNotificationClick: () => void;
}

function StudentCard({
  student,
  validityStatus,
  onManageClick,
  onAvaliationClick,
  onNotificationClick,
}: StudentCardProps) {
  return (
    <div className="group relative overflow-hidden rounded-xl border border-stroke bg-white p-3 shadow-sm transition-all touch-manipulation dark:border-strokedark dark:bg-boxdark active:scale-[0.99] sm:rounded-2xl sm:p-6 sm:active:scale-100 md:hover:shadow-lg">
      {/* Status Badge - Mobile First */}
      <div className="absolute right-2 top-2 sm:right-4 sm:top-4">
        <span
          className={`inline-flex items-center rounded-full px-2 py-1 text-[10px] font-semibold sm:px-3 sm:py-1 sm:text-xs ${
            validityStatus.color === "green"
              ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
              : validityStatus.color === "yellow"
                ? "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400"
                : validityStatus.color === "orange"
                  ? "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400"
                  : validityStatus.color === "red"
                    ? "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
                    : "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400"
          }`}
        >
          {validityStatus.label}
        </span>
      </div>

      {/* Header do Card - Mobile First */}
      <div className="relative mb-3 flex items-start gap-2.5 pr-14 sm:mb-5 sm:gap-4 sm:pr-20 md:pr-0">
        <div className="relative flex-shrink-0">
          <div className="absolute -inset-0.5 rounded-full bg-gradient-to-r from-purple-600 to-purple-600 opacity-0 blur-sm transition-opacity group-hover:opacity-75 sm:-inset-1" aria-hidden />
          <div className="relative h-12 w-12 overflow-hidden rounded-full border-2 border-white shadow-md dark:border-boxdark sm:h-20 sm:w-20 sm:border-4">
            <img
              src={
                (student.user.photo
                  ? utils.getUriImage(student.user.photo)
                  : null) ||
                `https://ui-avatars.com/api/?name=${encodeURIComponent(
                  student.user.name,
                )}&background=85368c&color=fff&size=200`
              }
              alt={student.user.name}
              className="h-full w-full object-cover"
            />
          </div>
        </div>
        <div className="min-w-0 flex-1 pt-0 sm:pt-1">
          <h4 className="truncate text-base font-bold text-gray-900 dark:text-white sm:text-lg">
            {student.user.name}
          </h4>
          <p className="mt-0.5 flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400 sm:gap-1.5 sm:text-sm">
            <FiCalendar size={12} className="flex-shrink-0 sm:h-3.5 sm:w-3.5" />
            {utils.calcularIdade(
              new Date(student.user.birth_date || new Date()),
            )}{" "}
            anos
          </p>
        </div>
      </div>

      {/* Informações - Mobile First */}
      <div className="space-y-2 sm:space-y-2.5">
        <div className="flex items-center gap-2 rounded-xl bg-gray-50 px-2.5 py-2 dark:bg-gray-800/50 sm:px-3 sm:py-2.5">
          <FiMail className="flex-shrink-0 text-gray-400" size={14} />
          <span className="min-w-0 truncate text-xs text-gray-700 dark:text-gray-300 sm:text-sm">
            {student.user.email}
          </span>
        </div>

        <div className="flex flex-col gap-0.5 rounded-xl bg-purple-50 px-2.5 py-2 dark:bg-purple-900/20 sm:flex-row sm:items-center sm:justify-between sm:gap-2 sm:px-3 sm:py-2.5">
          <span className="text-[10px] font-medium text-purple-700 dark:text-purple-400 sm:text-sm">
            Validade do Plano
          </span>
          <span className="text-xs font-bold text-purple-900 dark:text-purple-300 sm:text-sm">
            {student.validity
              ? utils.formatDate(new Date(student.validity))
              : "Não definida"}
          </span>
        </div>

        <div className="grid grid-cols-4 gap-1.5 sm:gap-2 pt-0.5 sm:pt-1">
          <div className="flex flex-col items-center justify-center rounded-lg bg-gray-50 px-1 py-2 text-center dark:bg-gray-800/50 sm:p-2">
            <span className="text-[9px] sm:text-[10px] text-gray-500 dark:text-gray-400">Treinos</span>
            <span className="mt-0.5 text-xs font-bold tabular-nums text-gray-900 dark:text-white sm:text-sm">{student.workouts_count || 0}</span>
          </div>
          <div className="flex flex-col items-center justify-center rounded-lg bg-gray-50 px-1 py-2 text-center dark:bg-gray-800/50 sm:p-2">
            <span className="text-[9px] sm:text-[10px] text-gray-500 dark:text-gray-400">Agend.</span>
            <span className="mt-0.5 text-xs font-bold tabular-nums text-gray-900 dark:text-white sm:text-sm">{student.appointments_count || 0}</span>
          </div>
          <div className="flex flex-col items-center justify-center rounded-lg bg-gray-50 px-1 py-2 text-center dark:bg-gray-800/50 sm:p-2">
            <span className="text-[9px] sm:text-[10px] text-gray-500 dark:text-gray-400">Alim.</span>
            <span className="mt-0.5 text-xs font-bold tabular-nums text-gray-900 dark:text-white sm:text-sm">{student.foods_count || 0}</span>
          </div>
          <div className="flex flex-col items-center justify-center rounded-lg bg-gray-50 px-1 py-2 text-center dark:bg-gray-800/50 sm:p-2">
            <span className="text-[9px] sm:text-[10px] text-gray-500 dark:text-gray-400">Aval.</span>
            <span className="mt-0.5 text-xs font-bold tabular-nums text-gray-900 dark:text-white sm:text-sm">{student.avaliations_count || 0}</span>
          </div>
        </div>
      </div>

      {/* Botões de Ação - Mobile First: área de toque 44px */}
      <div className="mt-3 flex flex-col gap-2 sm:mt-5 sm:flex-row sm:items-center sm:gap-3">
        <button
          type="button"
          onClick={onManageClick}
          className="w-full min-h-[44px] rounded-xl bg-gradient-to-r from-purple-600 to-purple-600 py-2.5 text-sm font-semibold text-white shadow-md transition-all active:scale-[0.98] sm:flex-1 sm:min-h-0 sm:py-2.5 md:hover:scale-[1.02] md:hover:shadow-lg touch-manipulation"
        >
          Gerenciar
        </button>
        <div className="flex gap-2 sm:flex-row sm:items-center sm:gap-2">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onAvaliationClick();
            }}
            title="Avaliação corporal"
            className="flex flex-1 items-center justify-center gap-2 min-h-[44px] rounded-xl bg-emerald-50 py-2.5 text-emerald-600 shadow-sm transition-all active:scale-[0.98] dark:bg-emerald-900/25 dark:text-emerald-400 sm:flex-none sm:min-h-0 sm:w-10 sm:max-w-[52px] sm:gap-0 sm:py-0 md:hover:bg-emerald-100 md:hover:shadow-md touch-manipulation"
            aria-label="Ver avaliação corporal"
          >
            <FiActivity size={18} className="flex-shrink-0 sm:h-5 sm:w-5" />
            <span className="text-xs font-medium sm:hidden">Avaliação</span>
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onNotificationClick();
            }}
            title="Enviar notificação"
            className="flex flex-1 items-center justify-center gap-2 min-h-[44px] rounded-xl bg-amber-50 py-2.5 text-amber-600 shadow-sm transition-all active:scale-[0.98] dark:bg-amber-900/25 dark:text-amber-400 sm:flex-none sm:min-h-0 sm:w-10 sm:max-w-[52px] sm:gap-0 sm:py-0 md:hover:bg-amber-100 md:hover:shadow-md touch-manipulation"
            aria-label="Notificações"
          >
            <FiBell size={18} className="flex-shrink-0 sm:h-5 sm:w-5" />
            <span className="text-xs font-medium sm:hidden">Notificação</span>
          </button>
        </div>
      </div>
    </div>
  );
}

export default StudentCard;
